import { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchInventoryValueMovementPreview,
  fetchInventoryValueReportPreview,
  isReportRequestCanceled,
} from "../api/inventory-value-report-api";

const IDLE_STATE = { status: "idle", document: null, error: null, loading: false };

/**
 * Latest-request-wins loader for a backend HTML document. A new request aborts the
 * previous one, and a monotonically increasing request ID rejects late responses even
 * when cancellation races. Changing `scopeKey` (outlet or user) clears everything.
 */
export const useReportDocumentRequest = (fetchDocument, scopeKey) => {
  const [state, setState] = useState(IDLE_STATE);
  const requestIdRef = useRef(0);
  const controllerRef = useRef(null);

  const cancel = useCallback(() => {
    requestIdRef.current += 1;
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  useEffect(() => {
    cancel();
    setState(IDLE_STATE);
  }, [scopeKey, cancel]);

  useEffect(() => cancel, [cancel]);

  const load = useCallback(
    async ({ outletId, body, context = null, keepDocumentOnError = false }) => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const requestId = ++requestIdRef.current;

      setState((previous) => {
        const keep = keepDocumentOnError && previous.document;
        return {
          status: keep ? previous.status : "loading",
          document: keep ? previous.document : null,
          error: null,
          loading: true,
        };
      });

      try {
        const { html, metadata } = await fetchDocument({
          outletId,
          body,
          signal: controller.signal,
        });
        if (requestId !== requestIdRef.current) return { status: "stale" };

        // The applied document and its metadata are replaced atomically on success.
        setState({
          status: metadata.rowCount === 0 ? "empty" : "success",
          document: { html, metadata, body, outletId, context },
          error: null,
          loading: false,
        });
        return { status: "success", metadata };
      } catch (error) {
        if (
          requestId !== requestIdRef.current ||
          isReportRequestCanceled(error)
        ) {
          return { status: "stale" };
        }
        setState((previous) =>
          keepDocumentOnError && previous.document
            ? { ...previous, error, loading: false }
            : { status: "error", document: null, error, loading: false }
        );
        return { status: "error", error };
      } finally {
        if (controllerRef.current === controller) controllerRef.current = null;
      }
    },
    [fetchDocument]
  );

  return { ...state, load };
};

export const useInventoryValueReport = ({ outletId, userId }) =>
  useReportDocumentRequest(
    fetchInventoryValueReportPreview,
    `${outletId ?? ""}:${userId ?? ""}`
  );

export const useInventoryValueMovementDetail = ({ outletId, userId }) =>
  useReportDocumentRequest(
    fetchInventoryValueMovementPreview,
    `${outletId ?? ""}:${userId ?? ""}`
  );
